import { Component } from "@theme/component-base";

/**
 * Swatches Item Component - Optimized for Performance
 * 
 * Handles color swatch interactions on product cards
 * - Uses event delegation for minimal listeners
 * - Preloads variant images on hover for instant switching
 * - CSS transitions instead of JS delays
 * - Minimal memory footprint
 */
class SwatchesItemComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Check if this is standalone (product page) or collection page
    this.isStandalone = this.hasAttribute('data-standalone');
    this.isGroupedSwatches = this.hasAttribute('data-grouped-swatches');
    this.productCard = this.closest('[data-product-card]');
    this.preloadedImages = new Set();
    this.#loadGroupedProductsMap();
    this.#syncSelectedSwatchWithCurrentPath();
    this.#enhanceUnavailableSwatches();
    this.#setupTooltipPositioning();

    // Set up "Show all" button functionality
    this.#setupShowAllButton();

    // Grouped swatches on PDP (inside variant picker, no product card): navigate to linked product
    if (this.isGroupedSwatches && !this.isStandalone && !this.productCard) {
      this.#setupProductPageGroupedNavigation();
    }

    // For collection pages, we need the product card for image switching
    if (!this.isStandalone && !this.productCard) {
      return;
    }

    // Only set up image switching for collection page cards
    if (!this.isStandalone && this.productCard) {
      // Cache image elements (don't store full data, just references)
      this.productImages = this.productCard.querySelectorAll('[data-product-image]');
      if (this.productImages.length === 0) {
        return;
      }

      // Store ONLY original src/srcset as strings (minimal memory)
      this.originalSrc = this.productImages[0]?.src;
      this.originalSrcset = this.productImages[0]?.srcset;
      this.originalAlt = this.productImages[0]?.alt;

      // Track preloaded images for instant switching
      this.#setupEventDelegation();
    }
  }

  /**
   * Wraps unavailable swatches for hover tooltips (disabled buttons block native title tooltips).
   */
  #enhanceUnavailableSwatches() {
    if (this.hasAttribute('data-filter-swatch')) return;

    const oosLabel = this.dataset.oosLabel || 'Out of stock';

    this.querySelectorAll('[data-swatch-unavailable="true"]').forEach((button) => {
      if (button.closest('.swatch-tooltip-wrap')) return;

      const swatchValue = button.dataset.swatchValue || button.dataset.value || '';
      button.removeAttribute('title');
      if (swatchValue) {
        button.setAttribute('aria-label', `${swatchValue} — ${oosLabel}`);
      }

      const wrap = document.createElement('span');
      wrap.className = 'swatch-tooltip-wrap relative inline-flex';

      const tooltip = document.createElement('span');
      tooltip.className = 'swatch-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      tooltip.textContent = oosLabel;

      button.parentNode?.insertBefore(wrap, button);
      wrap.append(button, tooltip);
    });
  }

  #setupTooltipPositioning() {
    const card = this.closest('[data-product-card]') || this.closest('.card-padding')?.parentElement || this;

    this.querySelectorAll('.swatch-tooltip-wrap').forEach((wrap) => {
      const tooltip = wrap.querySelector('.swatch-tooltip');
      if (!tooltip) return;

      wrap.addEventListener('mouseenter', () => {
        tooltip.style.left = '';
        tooltip.style.transform = '';

        requestAnimationFrame(() => {
          const tipRect = tooltip.getBoundingClientRect();
          const cardRect = card.getBoundingClientRect();

          if (tipRect.left < cardRect.left) {
            const shift = cardRect.left - tipRect.left;
            tooltip.style.left = `calc(50% + ${shift}px)`;
          } else if (tipRect.right > cardRect.right) {
            const shift = tipRect.right - cardRect.right;
            tooltip.style.left = `calc(50% - ${shift}px)`;
          }
        });
      });

      wrap.addEventListener('mouseleave', () => {
        tooltip.style.left = '';
        tooltip.style.transform = '';
      });
    });
  }

  #loadGroupedProductsMap() {
    if (!this.isGroupedSwatches) return;

    const jsonEl = this.querySelector('[data-grouped-products-json]');
    if (!jsonEl) return;

    try {
      const products = JSON.parse(jsonEl.textContent);
      this.groupedProductsByUrl = new Map(
        products.map((product) => [this.#normalizePath(product.url), product])
      );
      this.groupedProductsByHandle = new Map(
        products.map((product) => [product.handle, product])
      );
    } catch (_) {
      this.groupedProductsByUrl = new Map();
    }
  }

  #normalizePath(url) {
    if (!url) return '';
    return url.split('?')[0].replace(/\/+$/, '');
  }

  #syncSelectedSwatchWithCurrentPath() {
    const swatchLinks = this.querySelectorAll('[data-swatch-button][data-product-url]');
    if (!swatchLinks.length) return;

    const currentPath = window.location.pathname.replace(/\/+$/, '');
    let hasSelected = false;

    swatchLinks.forEach((swatch) => {
      const url = swatch.getAttribute('data-product-url');
      if (!url) return;
      const targetPath = this.#normalizePath(url);
      if (targetPath === currentPath) {
        swatch.setAttribute('data-selected', 'true');
        hasSelected = true;
      } else {
        swatch.removeAttribute('data-selected');
      }
    });

    // Fallback to first swatch when no URL match is found
    if (!hasSelected) {
      swatchLinks[0].setAttribute('data-selected', 'true');
    }
  }

  /**
   * Grouped colour swatches on the product page (buttons without a product card).
   */
  #setupProductPageGroupedNavigation() {
    this.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-swatch-button]');
      if (!swatch || !this.contains(swatch) || swatch.hasAttribute('data-swatch-unavailable')) return;

      const productUrl = swatch.dataset.productUrl;
      if (!productUrl) return;

      e.preventDefault();
      window.location.href = productUrl;
    }, true);
  }

  /**
   * Single event listener using delegation - optimal for performance
   */
  #setupEventDelegation() {
    // FIRST: Block parent link clicks at the card level (capture phase)
    // This fires BEFORE any child handlers
    this.productCard.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-swatch-button]');
      if (swatch?.hasAttribute('data-swatch-unavailable')) return;
      if (swatch && this.contains(swatch)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Manually trigger our handler since we stopped propagation
        this.#handleSwatchClick(swatch);
        return false;
      }
    }, true);

    // Preload on hover for instant switching (UX optimization)
    this.addEventListener('mouseenter', (e) => {
      const button = e.target.closest('[data-swatch-button]');
      if (!button || button.hasAttribute('data-swatch-unavailable')) return;

      if (this.isGroupedSwatches && button.dataset.productUrl) {
        const productData = this.groupedProductsByUrl?.get(this.#normalizePath(button.dataset.productUrl));
        const preloadSrc = productData?.featuredImage?.src;
        if (preloadSrc) {
          this.#preloadImage(preloadSrc);
        }
        return;
      }

      if (button.dataset.variantImage) {
        this.#preloadImage(button.dataset.variantImage);
      }
    }, true);
  }

  /**
   * Preload image for instant switching
   */
  #preloadImage(src) {
    if (this.preloadedImages.has(src)) return;
    
    const img = new Image();
    img.src = src;
    this.preloadedImages.add(src);
  }

  /**
   * Handles swatch click - instant image switch (already preloaded on hover)
   */
  #handleSwatchClick(button) {
    if (button.hasAttribute('data-swatch-unavailable')) return;

    const productUrl = button.dataset.productUrl;

    if (productUrl && this.isGroupedSwatches && !this.isStandalone) {
      const productData =
        this.groupedProductsByUrl?.get(this.#normalizePath(productUrl)) ||
        this.groupedProductsByHandle?.get(button.dataset.productHandle);
      if (productData) {
        this.#updateGroupedProductCard(productData);
        this.#setSelectedSwatch(button);
        return;
      }
    }

    if (productUrl && this.isStandalone) {
      window.location.href = productUrl;
      return;
    }

    const variantImage = button.dataset.variantImage;
    const variantImageSrcset = button.dataset.variantImageSrcset;
    const variantImageAlt = button.dataset.variantImageAlt;
    const hasSlider = this.closest('[data-product-card]')?.querySelector('product-media-slider');

    if (!variantImage) {
      return;
    }

    if (!hasSlider) {
      this.#updateImage(variantImage, variantImageSrcset, variantImageAlt);
    }

    const mediaId = button.dataset.mediaId;
    if (mediaId) {
      this.dispatchEvent(new CustomEvent('swatch:change', {
        bubbles: true,
        detail: {
          mediaId: mediaId,
          variantImage: variantImage
        }
      }));
    }

    this.#setSelectedSwatch(button);
  }

  #setSelectedSwatch(button) {
    const swatchButtons = this.querySelectorAll('[data-swatch-button]');
    swatchButtons.forEach((btn) => {
      btn.removeAttribute('data-selected');
    });
    button.setAttribute('data-selected', 'true');
  }

  #updateGroupedProductCard(productData) {
    if (!this.productCard || !productData) return;

    const featuredImage = productData.featuredImage;
    const images = productData.images?.length ? productData.images : featuredImage ? [featuredImage] : [];
    const slider = this.productCard.querySelector('product-media-slider');

    if (slider && images.length) {
      slider.dispatchEvent(new CustomEvent('product-card:images-change', {
        bubbles: false,
        detail: { images }
      }));
    } else if (featuredImage?.src) {
      this.#updateImage(featuredImage.src, featuredImage.srcset, featuredImage.alt);
    }

    this.#updateCardLinks(productData.url);
    this.#updateCardTitle(productData.title);
    this.#updateCardPrice(productData);
    this.#updateQuickBuyTrigger(productData);

    this.dataset.productUrl = productData.url;
    this.dataset.productId = String(productData.id);

    const cardWrapper = this.productCard.closest('[data-product-id]');
    if (cardWrapper) {
      cardWrapper.dataset.productId = String(productData.id);
    }
  }

  #updateCardLinks(url) {
    if (!url || !this.productCard) return;

    this.productCard.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.href = url;
    });
  }

  #updateCardTitle(title) {
    if (!title || !this.productCard) return;

    const titleEls = this.productCard.querySelectorAll('[class*="-title"].title');
    const productTitleEl = titleEls.length > 1 ? titleEls[1] : titleEls[0];
    if (!productTitleEl) return;

    productTitleEl.textContent = title;

    const duplicateParagraph = productTitleEl.parentElement?.querySelector(':scope > p');
    if (duplicateParagraph) {
      duplicateParagraph.textContent = title;
    }
  }

  #updateCardPrice(productData) {
    if (!this.productCard) return;

    const priceEl = this.productCard.querySelector('.price-current');
    const compareEl = this.productCard.querySelector('.price-compare');
    const savingsEl = this.productCard.querySelector('[ref="priceSavings"]');

    if (priceEl && productData.price) {
      priceEl.textContent = productData.price;
      priceEl.classList.toggle('text-sale', productData.onSale);
    }

    if (compareEl) {
      if (productData.onSale && productData.compareAtPrice) {
        compareEl.textContent = productData.compareAtPrice;
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }

    if (savingsEl) {
      savingsEl.classList.toggle('hidden', !productData.onSale);
    }
  }

  #updateQuickBuyTrigger(productData) {
    const trigger = this.productCard?.querySelector('[data-quick-buy-trigger]');
    if (!trigger) return;

    trigger.dataset.productHandle = productData.handle;
    trigger.dataset.productId = String(productData.id);
    trigger.dataset.variantId = String(productData.variantId);
    trigger.dataset.singleVariant = productData.singleVariant ? 'true' : 'false';
    trigger.hidden = !productData.available;
  }

  /**
   * Updates ALL product images instantly (already cached from hover)
   */
  #updateImage(src, srcset, alt) {
    if (this.productImages.length === 0) return;

    this.productImages.forEach((img) => {
      img.src = src;
      if (srcset) {
        img.srcset = srcset;
      }
      img.alt = alt || this.originalAlt || '';
    });
  }

  /**
   * Set up "Show all" button functionality
   * Reveals hidden swatches when clicked
   */
  #setupShowAllButton() {
    const showAllButton = this.querySelector('[data-show-all-button]');
    if (!showAllButton) return;

    const action = showAllButton.dataset.action;

    // If action is "url", it's a link - no JS needed
    if (action === 'url') return;

    // If action is "expand", reveal hidden swatches
    if (action === 'expand') {
      showAllButton.addEventListener('click', (e) => {
        // Prevent click from bubbling to parent product card link
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Find all hidden swatches within THIS component instance
        const hiddenSwatches = this.querySelectorAll('[data-hidden-swatch]');
        
        // Reveal them with a simple class toggle
        hiddenSwatches.forEach(swatch => {
          swatch.classList.remove('hidden');
          swatch.removeAttribute('data-hidden-swatch');
        });

        // Remove the button after expanding
        showAllButton.remove();
        
        return false;
      }, true); // Use capture phase to prevent parent handlers
    }
  }
}

// Register component
if (!customElements.get('swatches-item-component')) {
  customElements.define('swatches-item-component', SwatchesItemComponent);
}
